import { useNavigate } from "react-router-dom";

// MUI
import {
    AccessTime,
    CardMembership,
    Chat,
    Email,
    Groups,
    LocationOn,
    Phone,
    Psychology,
    School,
    Speed,
    SupportAgent,
    WhatsApp
} from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Stack,
    Typography,
    useTheme
} from "@mui/material";

export default function Contact() {
    const theme = useTheme();
    const navi = useNavigate();

    const contactInfo = [
        {
            icon: Phone,
            title: "Call Us",
            primary: "+91 7808426759",
            secondary: "Mon-Sat, 9AM-6PM",
            action: "tel:7808426759",
            gradient: `linear-gradient(135deg, ${theme.palette.secondary.main}20, ${theme.palette.info.main}20)`,
        },
        {
            icon: Email,
            title: "Email Us",
            primary: "info@spywarecomputers.com",
            secondary: "We'll respond within 24hrs",
            action: "mailto:info@spywarecomputers.com",
            gradient: `linear-gradient(135deg, ${theme.palette.info.main}20, ${theme.palette.success.main}20)`,
        },
        {
            icon: LocationOn,
            title: "Visit Us",
            primary: "Mirchaibari, Katihar - 854105",
            secondary: "Katihar, Bihar",
            action: null,
            gradient: `linear-gradient(135deg, ${theme.palette.success.main}20, ${theme.palette.warning.main}20)`,
        },
        {
            icon: AccessTime,
            title: "Working Hours",
            primary: "Mon - Sat",
            secondary: "9:00 AM - 6:00 PM",
            action: null,
            gradient: `linear-gradient(135deg, ${theme.palette.warning.main}20, ${theme.palette.secondary.main}20)`,
        },
    ];

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    position: "relative",
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    overflow: "hidden",
                    pt: { xs: 4, md: 0 },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "-50%",
                        right: "-20%",
                        width: "800px",
                        height: "800px",
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${theme.palette.secondary.main}25 0%, transparent 70%)`,
                        animation: "rotate 30s linear infinite",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-30%",
                        left: "-10%",
                        width: "600px",
                        height: "600px",
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${theme.palette.info.main}20 0%, transparent 70%)`,
                        animation: "rotate 25s linear infinite reverse",
                    },
                    "@keyframes rotate": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" }
                    }
                }}
            >
                {/* Floating geometric shapes */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "15%",
                        right: "10%",
                        width: "200px",
                        height: "200px",
                        background: `${theme.palette.secondary.main}15`,
                        borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
                        animation: "morph 8s ease-in-out infinite",
                        "@keyframes morph": {
                            "0%, 100%": { borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%" },
                            "50%": { borderRadius: "70% 30% 30% 70% / 70% 70% 30% 30%" }
                        }
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "20%",
                        left: "8%",
                        width: "150px",
                        height: "150px",
                        background: `${theme.palette.info.main}20`,
                        borderRadius: "50%",
                        animation: "float 6s ease-in-out infinite",
                        "@keyframes float": {
                            "0%, 100%": { transform: "translateY(0px)" },
                            "50%": { transform: "translateY(-30px)" }
                        }
                    }}
                />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">
                        {/* Left Content */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={4}>
                                {/* Badge */}
                                <Box
                                    sx={{
                                        animation: "slideInLeft 0.8s ease-out",
                                        "@keyframes slideInLeft": {
                                            from: { opacity: 0, transform: "translateX(-50px)" },
                                            to: { opacity: 1, transform: "translateX(0)" }
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 1,
                                            px: 2,
                                            py: 0.8,
                                            borderRadius: 50,
                                            background: `linear-gradient(135deg, ${theme.palette.secondary.main}30, ${theme.palette.info.main}30)`,
                                            border: `1px solid ${theme.palette.secondary.main}50`,
                                            backdropFilter: "blur(10px)",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                background: theme.palette.secondary.main,
                                                animation: "pulse 2s ease-in-out infinite",
                                                "@keyframes pulse": {
                                                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                                                    "50%": { opacity: 0.5, transform: "scale(1.2)" }
                                                }
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: theme.palette.secondary.main,
                                                fontWeight: 600,
                                                fontSize: { xs: "0.7rem", md: "0.75rem" },
                                                letterSpacing: "0.5px"
                                            }}
                                        >
                                            GET IN TOUCH
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Main Heading */}
                                <Box
                                    sx={{
                                        animation: "slideInLeft 0.8s ease-out 0.2s both",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                                            fontWeight: 900,
                                            color: "#ffffff",
                                            lineHeight: 1.1,
                                            mb: 1.5,
                                            background: `linear-gradient(135deg, #ffffff 0%, ${theme.palette.secondary.main} 100%)`,
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Let's Start a Conversation
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: "0.95rem", md: "1.15rem" },
                                            color: "rgba(255, 255, 255, 0.85)",
                                            lineHeight: 1.7,
                                            fontWeight: 400,
                                            maxWidth: "550px"
                                        }}
                                    >
                                        Have questions about our courses? Want to discuss your learning goals? We're here to help you take the next step in your career journey.
                                    </Typography>
                                </Box>

                                {/* Quick Contact Stats */}
                                <Box
                                    sx={{
                                        animation: "slideInLeft 0.8s ease-out 0.4s both",
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        {[
                                            { icon: SupportAgent, label: "24/7 Support", value: "Available" },
                                            { icon: Chat, label: "Response Time", value: "< 2 Hours" },
                                        ].map((stat, index) => {
                                            const Icon = stat.icon;
                                            return (
                                                <Grid item xs={6} key={index}>
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2,
                                                            background: "rgba(255, 255, 255, 0.08)",
                                                            backdropFilter: "blur(20px)",
                                                            border: `1px solid ${theme.palette.secondary.main}25`,
                                                            transition: "all 0.3s ease",
                                                            "&:hover": {
                                                                background: "rgba(255, 255, 255, 0.12)",
                                                                transform: "translateY(-5px)",
                                                            }
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                                            <Box
                                                                sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    borderRadius: 1.5,
                                                                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}30, ${theme.palette.info.main}30)`,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                <Icon sx={{ fontSize: "1.3rem", color: theme.palette.secondary.main }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "0.75rem",
                                                                        color: "rgba(255, 255, 255, 0.6)",
                                                                        fontWeight: 500
                                                                    }}
                                                                >
                                                                    {stat.label}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "1rem",
                                                                        fontWeight: 700,
                                                                        color: "#ffffff",
                                                                    }}
                                                                >
                                                                    {stat.value}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>

                                {/* CTA Buttons */}
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={2}
                                    sx={{
                                        animation: "slideInLeft 0.8s ease-out 0.6s both",
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        color="secondary"
                                        startIcon={<Phone />}
                                        onClick={() => window.location.href = "tel:7808426759"}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: "0.95rem",
                                            px: 3.5,
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: "none",
                                            color: theme.palette.primary.main,
                                            boxShadow: `0 8px 24px ${theme.palette.secondary.main}40`,
                                            transition: "all 0.3s ease",
                                            position: "relative",
                                            overflow: "hidden",
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                left: "-100%",
                                                width: "100%",
                                                height: "100%",
                                                background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)`,
                                                transition: "left 0.5s",
                                            },
                                            "&:hover": {
                                                transform: "translateY(-3px)",
                                                boxShadow: `0 12px 32px ${theme.palette.secondary.main}60`,
                                                "&::before": {
                                                    left: "100%",
                                                }
                                            }
                                        }}
                                    >
                                        Call Now
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        startIcon={<WhatsApp />}
                                        onClick={() => window.open("https://wa.me/917808426759", "_blank")}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: "0.95rem",
                                            px: 3.5,
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: "none",
                                            borderWidth: 2,
                                            borderColor: "rgba(255, 255, 255, 0.4)",
                                            color: "#ffffff",
                                            backdropFilter: "blur(10px)",
                                            background: "rgba(255, 255, 255, 0.08)",
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                borderWidth: 2,
                                                borderColor: theme.palette.secondary.main,
                                                background: "rgba(255, 255, 255, 0.15)",
                                                transform: "translateY(-3px)"
                                            }
                                        }}
                                    >
                                        Chat on WhatsApp
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>

                        {/* Right Contact Cards */}
                        <Grid item xs={12} md={6}>
                            <Stack
                                spacing={2}
                                sx={{
                                    animation: "slideInRight 0.8s ease-out 0.4s both",
                                    "@keyframes slideInRight": {
                                        from: { opacity: 0, transform: "translateX(50px)" },
                                        to: { opacity: 1, transform: "translateX(0)" }
                                    }
                                }}
                            >
                                {contactInfo.map((info, index) => {
                                    const Icon = info.icon;
                                    return (
                                        <Card
                                            key={index}
                                            onClick={() => info.action && (window.location.href = info.action)}
                                            sx={{
                                                background: "rgba(255, 255, 255, 0.1)",
                                                backdropFilter: "blur(20px)",
                                                border: `1px solid ${theme.palette.secondary.main}30`,
                                                borderRadius: 3,
                                                transition: "all 0.3s ease",
                                                cursor: info.action ? "pointer" : "default",
                                                "&:hover": {
                                                    transform: info.action ? "translateX(-10px)" : "none",
                                                    background: "rgba(255, 255, 255, 0.15)",
                                                    boxShadow: `0 8px 32px ${theme.palette.secondary.main}30`,
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: 2.5 }}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Box
                                                        sx={{
                                                            width: 60,
                                                            height: 60,
                                                            borderRadius: 2,
                                                            background: info.gradient,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Icon sx={{ fontSize: "1.8rem", color: theme.palette.secondary.main }} />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.85rem",
                                                                color: "rgba(255, 255, 255, 0.6)",
                                                                fontWeight: 500,
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            {info.title}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "1.1rem",
                                                                fontWeight: 700,
                                                                color: "#ffffff",
                                                                mb: 0.3
                                                            }}
                                                        >
                                                            {info.primary}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.8rem",
                                                                color: "rgba(255, 255, 255, 0.5)",
                                                                fontWeight: 400
                                                            }}
                                                        >
                                                            {info.secondary}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Why Contact Us Section */}
            <Box
                sx={{
                    position: "relative",
                    py: { xs: 8, md: 12 },
                    background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.main}08 100%)`,
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "10%",
                        right: "-5%",
                        width: "300px",
                        height: "300px",
                        borderRadius: "50%",
                        background: `${theme.palette.secondary.main}15`,
                        filter: "blur(60px)",
                        animation: "float 10s ease-in-out infinite",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "15%",
                        left: "-5%",
                        width: "250px",
                        height: "250px",
                        borderRadius: "50%",
                        background: `${theme.palette.info.main}15`,
                        filter: "blur(60px)",
                        animation: "float 8s ease-in-out infinite reverse",
                    },
                    "@keyframes float": {
                        "0%, 100%": { transform: "translateY(0px)" },
                        "50%": { transform: "translateY(-20px)" }
                    }
                }}
            >
                <Container maxWidth="lg">
                    <Stack>
                        {/* Section Header */}
                        <Box
                            sx={{
                                textAlign: "center",
                                animation: "fadeIn 0.8s ease-out",
                                "@keyframes fadeIn": {
                                    from: { opacity: 0 },
                                    to: { opacity: 1 }
                                }
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                                    color: theme.palette.secondary.main,
                                    fontWeight: 700,
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                    mb: 2,
                                }}
                            >
                                Why Contact Us
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "2rem", md: "3rem" },
                                    fontWeight: 900,
                                    color: theme.palette.text.primary,
                                    mb: 2,
                                }}
                            >
                                We're Here to Help You Succeed
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                                    color: theme.palette.text.secondary,
                                    maxWidth: "700px",
                                    mx: "auto",
                                }}
                            >
                                Get personalized guidance and support for your educational journey
                            </Typography>
                        </Box>

                        {/* Features Grid */}
                        <Grid container spacing={3} mt={5} >
                            {[
                                {
                                    icon: SupportAgent,
                                    title: "Expert Guidance",
                                    description: "Get advice from experienced counselors who understand your career goals",
                                    iconColor: "#8B5CF6" // Vibrant Purple
                                },
                                {
                                    icon: Speed,
                                    title: "Quick Response",
                                    description: "We respond to all inquiries within 2 hours during business hours",
                                    iconColor: "#06B6D4" // Cyan
                                },
                                {
                                    icon: Psychology,
                                    title: "Personalized Solutions",
                                    description: "Customized course recommendations based on your interests and goals",
                                    iconColor: "#10B981" // Emerald Green
                                },
                                {
                                    icon: Groups,
                                    title: "Career Counseling",
                                    description: "Free career guidance to help you choose the right path",
                                    iconColor: "#F59E0B" // Amber
                                },
                                {
                                    icon: School,
                                    title: "Course Information",
                                    description: "Detailed information about all our programs and certifications",
                                    iconColor: "#F43F5E" // Rose/Pink
                                },
                                {
                                    icon: CardMembership,
                                    title: "Admission Support",
                                    description: "Step-by-step assistance with the enrollment process",
                                    iconColor: "#6366F1" // Indigo
                                },
                            ].map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card
                                            sx={{
                                                height: "100%",
                                                background: "transparent",
                                                borderRadius: 3,
                                                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                                cursor: "pointer",
                                                position: "relative",
                                                overflow: "hidden",
                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                                "@media (hover: hover)": {
                                                    "&:hover": {
                                                        transform: "translateY(-8px)",
                                                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                                                    }
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: { xs: 2.5, md: 2.5 }, position: "relative", zIndex: 1, textAlign: "center" }}>
                                                <Stack spacing={1.5} alignItems="center">
                                                    {/* Icon */}
                                                    <Box
                                                        sx={{
                                                            width: { xs: 60, md: 70 },
                                                            height: { xs: 60, md: 70 },
                                                            borderRadius: 3,
                                                            background: `linear-gradient(135deg, ${feature.iconColor}30, ${feature.iconColor}10)`,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            transition: "transform 0.3s ease",
                                                            "&:hover": {
                                                                transform: "rotate(5deg) scale(1.1)",
                                                            }
                                                        }}
                                                    >
                                                        <Icon sx={{ fontSize: "2.2rem", color: feature.iconColor }} />
                                                    </Box>

                                                    {/* Content */}
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "1.2rem",
                                                                fontWeight: 700,
                                                                color: theme.palette.text.primary,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {feature.title}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.85rem",
                                                                color: theme.palette.text.secondary,
                                                                lineHeight: 1.5,
                                                            }}
                                                        >
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {/* Bottom CTA */}
                        <Box
                            sx={{
                                textAlign: "center",
                                pt: 4,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    mb: 3,
                                }}
                            >
                                Ready to take the next step?
                            </Typography>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                justifyContent="center"
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    color="secondary"
                                    startIcon={<Phone />}
                                    onClick={() => window.location.href = "tel:7808426759"}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: "0.95rem",
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        color: theme.palette.primary.main,
                                        boxShadow: `0 8px 24px ${theme.palette.secondary.main}40`,
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: `0 12px 32px ${theme.palette.secondary.main}60`,
                                        }
                                    }}
                                >
                                    Call Us Today
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<WhatsApp />}
                                    onClick={() => window.open("https://wa.me/917808426759", "_blank")}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: "0.95rem",
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        borderWidth: 2,
                                        borderColor: theme.palette.secondary.main,
                                        color: theme.palette.secondary.main,
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            borderWidth: 2,
                                            background: `${theme.palette.secondary.main}10`,
                                            transform: "translateY(-3px)"
                                        }
                                    }}
                                >
                                    Start a Chat
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                </Container>
            </Box>
        </>
    );
}